import { Warning } from '@mui/icons-material'
import {
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import type { InternalLabelMasterListItem } from '@ulysses-inc/harami_api_client'
import { formatInTimeZone } from 'date-fns-tz'
import { type FC, useRef } from 'react'
import { Link } from 'react-router-dom'
import { QRCodeModal } from './QRCodeModal'
import { RowActionPopover } from './RowActionPopover'
import { StatusBadge } from './StatusBadge'

// MEMO: ハードコードせずに中央集権的に管理する仕組みが必要
const getEditPath = (id: string) => `/labelMasters/${id}/edit`

type Props = {
  labelMaster: InternalLabelMasterListItem
}

const renderTruncatedText = (value: string | null | undefined) => {
  if (!value) {
    return (
      <Typography variant="body2" component="span">
        -
      </Typography>
    )
  }

  const shouldTruncate = value.length > 15
  const displayText = shouldTruncate ? `${value.slice(0, 15)}...` : value
  const content = (
    <Typography variant="body2" component="span" sx={{ whiteSpace: 'nowrap' }}>
      {displayText}
    </Typography>
  )

  if (!shouldTruncate) {
    return content
  }

  return (
    <Tooltip title={value} placement="top">
      <span>{content}</span>
    </Tooltip>
  )
}

export const Row: FC<Props> = ({
  labelMaster: {
    id,
    status,
    name,
    code,
    barcodeId,
    createdAt,
    hasRequiredArea,
  },
}) => {
  const editPath = getEditPath(id)
  const linkRef = useRef<HTMLAnchorElement>(null)

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement
    if (target.closest('a') || target.closest('button')) {
      return
    }
    linkRef.current?.click()
  }

  return (
    <TableRow
      onClick={handleRowClick}
      tabIndex={0}
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus': { bgcolor: 'action.hover' },
        '& > td': {
          py: 1,
          minWidth: 180,
        },
      }}
    >
      <TableCell>
        <Link
          ref={linkRef}
          to={editPath}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            color: 'inherit',
            textDecoration: 'none',
          }}
        >
          {name}
          {!hasRequiredArea && (
            <Tooltip
              placement="top"
              title={
                <Typography sx={{ fontSize: 10 }}>
                  「見た目が一致していることを判定」か
                  <br />
                  「位置補正基準」のどちらかが必要です
                </Typography>
              }
            >
              <IconButton
                size="small"
                sx={{ p: 0, ml: 0.5 }}
                aria-label="警告: 見た目が一致していることを判定か位置補正基準のどちらかが必要です"
                tabIndex={-1}
              >
                <Warning sx={{ fontSize: 16 }} color="warning" />
              </IconButton>
            </Tooltip>
          )}
        </Link>
      </TableCell>
      <TableCell>{renderTruncatedText(code)}</TableCell>
      <TableCell>{renderTruncatedText(barcodeId ?? null)}</TableCell>
      <TableCell>
        <StatusBadge status={status} />
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {formatInTimeZone(createdAt, 'Asia/Tokyo', 'yyyy/MM/dd H:mm')}
        </Typography>
      </TableCell>
      <TableCell>
        <QRCodeModal id={id} name={name} />
      </TableCell>
      <TableCell align="right">
        <RowActionPopover id={id} />
      </TableCell>
    </TableRow>
  )
}
